import React from 'react';
import { Helmet } from 'react-helmet';

const StructuredData = () => {
    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "MedicalBusiness",
        "@id": "https://ampedtherapeutics.com/#organization",
        "name": "Amped Therapeutics",
        "alternateName": "Amped Therapeutics Regenerative Medicine Center",
        "url": "https://ampedtherapeutics.com",
        "logo": "https://ampedtherapeutics.com/amped-icon.png",
        "description": "Florida's pioneering concierge regenerative medicine center specializing in Wharton's Jelly stem cell therapy, orthobiologics, and interventional pain management. Dr. Eugene Pereira brings world-class cellular therapies to premium facilities throughout Florida.",
        "email": "info@ampedtherapeutics.com",
        "address": {
            "@type": "PostalAddress",
            "addressLocality": "Florida",
            "addressRegion": "FL",
            "addressCountry": "US"
        },
        "areaServed": [
            {
                "@type": "City",
                "name": "Sarasota"
            },
            {
                "@type": "City",
                "name": "Fort Myers"
            },
            {
                "@type": "City",
                "name": "Naples"
            },
            {
                "@type": "City",
                "name": "Bradenton"
            },
            {
                "@type": "City",
                "name": "Venice"
            },
            {
                "@type": "State",
                "name": "Florida"
            }
        ],
        "medicalSpecialty": [
            "Pain Management",
            "Regenerative Medicine",
            "Interventional Pain Medicine",
            "Orthobiologics"
        ],
        "availableService": [
            {
                "@type": "MedicalProcedure",
                "name": "Wharton's Jelly Mesenchymal Stem Cell Therapy",
                "description": "Advanced Wharton's Jelly MSC therapy using ethically sourced stem cells for regenerative healing"
            },
            {
                "@type": "MedicalProcedure",
                "name": "MUSE Cell Therapy",
                "description": "Multilineage-differentiating stress-enduring cell therapy for comprehensive regeneration"
            },
            {
                "@type": "MedicalProcedure",
                "name": "PRP Therapy",
                "description": "Platelet-Rich Plasma injections for tissue regeneration and healing"
            },
            {
                "@type": "MedicalProcedure",
                "name": "BMAC Therapy",
                "description": "Bone Marrow Aspirate Concentrate for orthopedic conditions"
            },
            {
                "@type": "MedicalProcedure",
                "name": "Exosome Therapy",
                "description": "Advanced exosome treatment for cellular regeneration and communication"
            },
            {
                "@type": "MedicalTherapy",
                "name": "Orthopedic Regenerative Medicine",
                "description": "Treatment for knee, hip, shoulder arthritis and sports injuries"
            },
            {
                "@type": "MedicalTherapy",
                "name": "Neurological Recovery Therapy",
                "description": "Stem cell protocols for peripheral neuropathy and neurological conditions"
            },
            {
                "@type": "MedicalTherapy",
                "name": "Autoimmune Disease Treatment",
                "description": "Regenerative therapy for rheumatoid arthritis, MS, lupus and other autoimmune conditions"
            }
        ],
        "sameAs": [
            "https://www.facebook.com/ampedtherapeutics",
            "https://www.instagram.com/ampedtherapeutics",
            "https://www.linkedin.com/company/ampedtherapeutics"
        ]
    };

    const physicianSchema = {
        "@context": "https://schema.org",
        "@type": "Physician",
        "@id": "https://ampedtherapeutics.com/#physician",
        "name": "Dr. Eugene G. Pereira",
        "honorificPrefix": "Dr.",
        "givenName": "Eugene",
        "familyName": "Pereira",
        "jobTitle": "Medical Director",
        "description": "Board-certified pain management physician with 25+ years of experience in regenerative medicine and interventional pain management. FIPP and ABIPP certified. Affiliated with Sarasota Memorial Hospital and Lee Memorial Hospital. Pioneer in bringing advanced cellular therapies to Florida through concierge medical model.",
        "medicalSpecialty": [
            "Pain Management",
            "Regenerative Medicine",
            "Interventional Pain Medicine",
            "Anesthesiology"
        ],
        "memberOf": {
            "@id": "https://ampedtherapeutics.com/#organization"
        },
        "alumniOf": [
            {
                "@type": "EducationalOrganization",
                "name": "St. John's Medical College",
                "address": {
                    "@type": "PostalAddress",
                    "addressLocality": "Bangalore",
                    "addressCountry": "India"
                }
            }
        ],
        "hasCredential": [
            {
                "@type": "EducationalOccupationalCredential",
                "credentialCategory": "Medical Degree",
                "name": "Doctor of Medicine (MD)"
            },
            {
                "@type": "EducationalOccupationalCredential",
                "credentialCategory": "Board Certification",
                "name": "Fellow of Interventional Pain Practice (FIPP)"
            },
            {
                "@type": "EducationalOccupationalCredential",
                "credentialCategory": "Board Certification",
                "name": "American Board of Interventional Pain Physicians (ABIPP)"
            }
        ],
        "knowsAbout": [
            "Wharton's Jelly Mesenchymal Stem Cell Therapy",
            "MUSE Cell Therapy",
            "Regenerative Medicine",
            "PRP Therapy",
            "BMAC Therapy",
            "Exosome Therapy",
            "Orthobiologics",
            "Pain Management",
            "Interventional Pain Procedures",
            "Orthopedic Regenerative Medicine",
            "Neurological Recovery",
            "Autoimmune Disease Treatment",
            "Concierge Medicine"
        ]
    };

    const localBusinessSchema = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": "https://ampedtherapeutics.com/#localbusiness",
        "name": "Amped Therapeutics",
        "image": "https://ampedtherapeutics.com/amped-icon.png",
        "url": "https://ampedtherapeutics.com",
        "email": "info@ampedtherapeutics.com",
        "priceRange": "$$$",
        "address": {
            "@type": "PostalAddress",
            "addressLocality": "Florida",
            "addressRegion": "FL",
            "addressCountry": "US"
        },
        "servesCuisine": "Concierge Medical Service",
        "openingHoursSpecification": [
            {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "09:00",
                "closes": "17:00"
            }
        ]
    };

    const webSiteSchema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": "https://ampedtherapeutics.com/#website",
        "url": "https://ampedtherapeutics.com",
        "name": "Amped Therapeutics",
        "description": "Advanced Regenerative Medicine Center",
        "publisher": {
            "@id": "https://ampedtherapeutics.com/#organization"
        },
        "potentialAction": {
            "@type": "SearchAction",
            "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://ampedtherapeutics.com/search?q={search_term_string}"
            },
            "query-input": "required name=search_term_string"
        }
    };

    return (
        <Helmet>
            <script type="application/ld+json">
                {JSON.stringify(organizationSchema)}
            </script>
            <script type="application/ld+json">
                {JSON.stringify(physicianSchema)}
            </script>
            <script type="application/ld+json">
                {JSON.stringify(localBusinessSchema)}
            </script>
            <script type="application/ld+json">
                {JSON.stringify(webSiteSchema)}
            </script>
        </Helmet>
    );
};

export default StructuredData;